import { Component, ContentChildren, Input, OnInit, QueryList } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { FormBase, Optional, boolean } from '@sb/library/base';
import { FormFieldCheckboxGroupItemComponent } from '../checkbox-group/form-field-checkbox-group-item.component';
import { FormFieldBaseComponent } from '../form-field-base.component';

class MutliSelectForm extends FormBase {
    SelectAll = Optional(boolean);
}

@Component({
    selector: 'lib-form-field-checkbox-multi-select',
    templateUrl: './form-field-checkbox-multi-select.component.html',
    styleUrl: './form-field-checkbox-multi-select.component.scss'
})
export class FormFieldCheckboxMultiSelectComponent <T extends keyof any> extends FormFieldBaseComponent<T[], FormArray<FormControl<boolean>>> implements OnInit {
    @ContentChildren(FormFieldCheckboxGroupItemComponent) itemQueryList!: QueryList<FormFieldCheckboxGroupItemComponent<T>>;
    @Input() selectAllLabel: string = $localize`:@@CommonTableSelectAllLabel:Select All`;

    readonly form = MutliSelectForm.Create();

    private _indexMap: Record<number, T> = {};
    private _recordMap: Record<T, number> = {} as any;
    private _disabledOverrideMap: Record<number, boolean> = {};
    private _isIndeterminate: boolean = false;
    private _currentKeys: T[] = [];


    constructor() {
        super();
        
        this.viewControl = new FormArray<FormControl<boolean>>([]);
    }

    SelectAll(state: boolean): void {
        let result: T[] = [];

        this.viewControl.controls.forEach((control, valueIndex) => {
            control.setValue(state);
            result.push(this._indexMap[valueIndex]);
        })
        this.ChangeEvent(state);
    }

    ngAfterContentInit(): void {
        this.itemQueryList!.forEach((item, index) => {
            this.viewControl.Add(this.formControl.value?.includes(item.value));
            // This approach does not allow for individual checkboxes to be disabled/reenabled dynamically. If this functionality
            // is needed at some point, revisit.
            if (item.disabled) {
                this.viewControl.controls[index].disable();
                this._disabledOverrideMap[index] = true;
            } else {
                this._disabledOverrideMap[index] = false;
            }

            this._indexMap[index] = item.value;
            this._recordMap[item.value] = index;
        });

        this.DetermineSelectAllCheckboxState(this._currentKeys);
    }

    override writeValue(keys: T[]): void {
        const allKeys = Object.keys(this._recordMap) as T[];
        allKeys.forEach(key => {
            this.viewControl.controls[this._recordMap[key]].setValue(keys?.includes(key));
        });

        this._currentKeys = keys;
    }

    override setDisabledState(state: boolean): void {
        Object.values(this.viewControl.controls).forEach((control, index) => {
            if (!this._disabledOverrideMap[index]) {
                if (state) {
                    control.disable();
                } else {
                    control.enable();
                }
            }
        });
    }

    override ObservePristine(): void {
        // This is done below in ChangeEvent instead
    }

    ChangeEvent(event: boolean, index: number | null = null): void {
        let result: T[] = [];

        // specify change to specific index
        if (index != null) {
            this.viewControl.controls[index].setValue(event);
        }
        
        this.viewControl.controls.forEach((control, valueIndex) => {
            if (control.value) {
                result.push(this._indexMap[valueIndex]);
            }
        })

        this.DetermineSelectAllCheckboxState(result);

        this.formControl.setValue(result);
        this.SetFormControlValue(result);

        this.formControl.isDirty = true;
        super.OnBlur();
    }

    private DetermineSelectAllCheckboxState(result: T[]) {
        this._isIndeterminate = result.length > 0 && result.length < this.itemQueryList.length;
        this.form.controls.SelectAll.setValue(result.length === this.itemQueryList.length);
    }

    get isIndeterminate(): boolean {
        return this._isIndeterminate;
    }

    get isAllSelected(): boolean {
        return this.form.controls.SelectAll.value;
    }

    get indexMap(): Record<number, T> {
        return this._indexMap;
    }
}
